import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Clock } from 'lucide-react';

export type CbtAnswer = string | string[];
export interface CbtQuestionData { id: number; type: string; body: string; media_path?: string | null; marks?: number | null; options?: { id: number; label?: string | null; body: string }[] }

const CHOICE = ['mcq', 'true_false'];

/** Normalises a saved server answer (id, ids or text) into the form state shape. */
export const toAnswer = (v: unknown): CbtAnswer | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  if (Array.isArray(v)) return v.map(String);
  return String(v);
};

/** Converts form state to the API value: option id (number), array of ids, or text. */
export const fromAnswer = (q: CbtQuestionData, v: CbtAnswer | undefined): unknown => {
  if (v === undefined) return null;
  if (q.type === 'multi') return (Array.isArray(v) ? v : [v]).map((x) => (Number.isFinite(Number(x)) ? Number(x) : x));
  if (CHOICE.includes(q.type)) return Number.isFinite(Number(v)) ? Number(v) : v;
  return Array.isArray(v) ? v.join(', ') : v;
};

export const isAnswered = (v: CbtAnswer | undefined) => v !== undefined && (Array.isArray(v) ? v.length > 0 : v.trim() !== '');

/** One question with the right input for its type. */
export function CbtQuestion({ q, index, value, onChange, disabled }: { q: CbtQuestionData; index: number; value: CbtAnswer | undefined; onChange: (v: CbtAnswer) => void; disabled?: boolean }) {
  const multi = q.type === 'multi';
  const vals = Array.isArray(value) ? value : value ? [value] : [];
  return (
    <article className="border rounded-xl p-4 space-y-3">
      <div className="flex justify-between gap-3">
        <h3 className="font-medium whitespace-pre-wrap">{index + 1}. {q.body}</h3>
        {q.marks ? <span className="text-xs text-slate-500 shrink-0">{q.marks} mark{q.marks === 1 ? '' : 's'}</span> : null}
      </div>
      {q.media_path && <img src={q.media_path} alt="" className="max-h-64 rounded-lg border" />}
      {CHOICE.includes(q.type) || multi ? (
        <div className="space-y-2">
          {(q.options ?? []).map((o) => {
            const checked = vals.includes(String(o.id));
            return (
              <label key={o.id} className={clsx('flex items-start gap-2 text-sm border rounded-lg px-3 py-2 cursor-pointer', checked ? 'border-brand-600 bg-blue-50' : 'border-slate-200')}>
                <input
                  type={multi ? 'checkbox' : 'radio'}
                  name={`q-${q.id}`}
                  checked={checked}
                  disabled={disabled}
                  onChange={(e) => onChange(multi ? (e.target.checked ? [...vals, String(o.id)] : vals.filter((x) => x !== String(o.id))) : String(o.id))}
                  className="mt-0.5"
                />
                <span>{o.label ? <b className="mr-1">{o.label}.</b> : null}{o.body}</span>
              </label>
            );
          })}
          {multi && <p className="text-xs text-slate-400">Select all that apply.</p>}
        </div>
      ) : (
        <textarea rows={q.type === 'essay' ? 6 : 2} disabled={disabled} className="border rounded-xl px-3 py-2 w-full text-sm" placeholder="Type your answer" value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />
      )}
    </article>
  );
}

/** Counts down to `deadline` (ISO). Calls `onExpire` once when it reaches zero. `offsetMs` corrects for client clock drift. */
export function Countdown({ deadline, onExpire, offsetMs = 0 }: { deadline: string; onExpire?: () => void; offsetMs?: number }) {
  const end = new Date(deadline).getTime();
  const [left, setLeft] = useState(() => Math.max(0, end - (Date.now() + offsetMs)));
  const fired = useRef(false);
  const cb = useRef(onExpire);
  cb.current = onExpire;
  useEffect(() => {
    const t = setInterval(() => {
      const l = Math.max(0, end - (Date.now() + offsetMs));
      setLeft(l);
      if (l === 0 && !fired.current) { fired.current = true; cb.current?.(); }
    }, 1000);
    return () => clearInterval(t);
  }, [end, offsetMs]);
  const s = Math.floor(left / 1000);
  const hh = Math.floor(s / 3600), mm = Math.floor((s % 3600) / 60), ss = s % 60;
  const p = (n: number) => String(n).padStart(2, '0');
  return (
    <span className={clsx('inline-flex items-center gap-1 font-mono text-sm px-2 py-1 rounded-lg', s <= 60 ? 'bg-red-100 text-red-700' : s <= 300 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700')}>
      <Clock size={14} /> {hh ? `${hh}:` : ''}{p(mm)}:{p(ss)}
    </span>
  );
}

/** Small navigator: numbered buttons, answered ones filled. */
export function QuestionNav({ count, current, answered, onPick }: { count: number; current: number; answered: (i: number) => boolean; onPick: (i: number) => void }) {
  return (
    <div className="flex flex-wrap gap-1">
      {Array.from({ length: count }, (_, i) => (
        <button key={i} type="button" onClick={() => onPick(i)} className={clsx('w-8 h-8 rounded-lg text-xs font-semibold border', i === current ? 'border-brand-600 ring-2 ring-brand-600/30' : 'border-slate-200', answered(i) ? 'bg-brand-600 text-white' : 'bg-white text-slate-600')}>
          {i + 1}
        </button>
      ))}
    </div>
  );
}
